import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "writer" ? "writer" : "buyer";
  const [form, setForm] = useState({ name: "", email: "", password: "", role: initialRole });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(form);
      toast.success(`Welcome, ${user.name}!`);
      navigate(user.role === "writer" ? "/dashboard/writer" : "/dashboard/buyer");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Signup failed");
    } finally { setLoading(false); }
  };

  const googleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const cls = "bg-black/40 border-white/10 focus:border-[#FF6B00] text-white placeholder:text-white/30 h-12";

  return (
    <div className="max-w-md mx-auto px-6 py-24">
      <div className="cw-label">Create account</div>
      <h1 className="font-display text-4xl mt-3">Start selling or shopping scripts.</h1>

      <div className="mt-6 grid grid-cols-2 gap-3">
        {["buyer", "writer"].map((r) => (
          <button
            key={r}
            type="button"
            data-testid={`role-${r}`}
            onClick={() => setForm({ ...form, role: r })}
            className={`p-4 rounded-xl border text-left transition ${form.role === r ? "border-[#FF6B00] bg-[#FF6B00]/10" : "border-white/10 hover:border-white/20"}`}
          >
            <div className="font-display capitalize">{r}</div>
            <div className="text-xs text-white/50 mt-1">{r === "buyer" ? "Purchase scripts" : "Sell your scripts"}</div>
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <Input required data-testid="signup-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={cls} />
        <Input required type="email" data-testid="signup-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={cls} />
        <Input required type="password" data-testid="signup-password" placeholder="Password (min 6 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={cls} />
        <button type="submit" disabled={loading} className="cw-btn-primary w-full h-12" data-testid="signup-submit">
          {loading ? "Creating account…" : "Create account"}
        </button>
      </form>
      <div className="my-6 flex items-center gap-3 text-white/40 text-xs">
        <div className="h-px bg-white/10 flex-1" /> OR <div className="h-px bg-white/10 flex-1" />
      </div>
      <button onClick={googleSignup} className="cw-btn-ghost w-full h-12" data-testid="signup-google">Continue with Google</button>
      <p className="text-center text-sm text-white/50 mt-6">
        Already have an account? <Link to="/login" className="text-[#FF6B00]" data-testid="link-login">Log in</Link>
      </p>
    </div>
  );
}
